import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

/**
 * Wiederverwendbares Signatur-Pad. Liefert beim Bestätigen eine PNG-Data-URL.
 */
const SignaturePad = ({
  label = 'Bitte hier unterschreiben',
  onConfirm,
  onCancel,
  confirmLabel = 'Unterschrift bestätigen',
  disabled = false,
}) => {
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [size, setSize] = useState({ width: 500, height: 180 });

  useEffect(() => {
    const updateSize = () => {
      if (wrapperRef.current) {
        const cw = wrapperRef.current.clientWidth;
        const w = cw > 50 ? Math.max(280, cw - 2) : 500;
        setSize({ width: w, height: 180 });
      }
    };
    updateSize();
    // Re-measure after modal entrance animation has settled.
    const t1 = setTimeout(updateSize, 80);
    const t2 = setTimeout(updateSize, 300);
    window.addEventListener('resize', updateSize);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const handleClear = () => {
    sigRef.current?.clear();
    setEmpty(true);
  };

  const checkEmpty = () => {
    if (!sigRef.current) return true;
    try {
      return sigRef.current.isEmpty();
    } catch {
      return true;
    }
  };

  const handleBegin = () => {
    // As soon as the user starts drawing, mark as non-empty so the submit
    // button is enabled — onEnd doesn't always fire reliably on mobile.
    setEmpty(false);
  };

  const handleEnd = () => {
    setEmpty(checkEmpty());
  };

  const handleConfirm = () => {
    if (!sigRef.current) return;
    // Don't block on isEmpty() — some mobile WebViews report empty even
    // after a valid signature. Try to export anyway and only fail if the
    // export itself is empty.
    let dataUrl = null;
    try {
      if (typeof sigRef.current.getTrimmedCanvas === 'function') {
        const trimmed = sigRef.current.getTrimmedCanvas();
        if (trimmed && typeof trimmed.toDataURL === 'function') {
          dataUrl = trimmed.toDataURL('image/png');
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Signature trim failed, using full canvas', err);
    }
    if (!dataUrl) {
      try {
        dataUrl = sigRef.current.toDataURL('image/png');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Signature export failed', err);
      }
    }
    if (!dataUrl || dataUrl.length < 200) {
      alert('Bitte unterschreiben Sie zuerst.');
      return;
    }
    onConfirm?.(dataUrl);
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div
        ref={wrapperRef}
        className="border-2 border-dashed border-gray-300 rounded-lg bg-white"
        style={{ touchAction: 'none' }}
      >
        <SignatureCanvas
          ref={sigRef}
          penColor="#0A0A0A"
          canvasProps={{
            width: size.width,
            height: size.height,
            className: 'rounded-lg',
            'data-testid': 'signature-canvas',
          }}
          onBegin={handleBegin}
          onEnd={handleEnd}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="btn-secondary text-xs"
          data-testid="signature-clear-button"
        >
          Löschen
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="btn-secondary text-xs"
            data-testid="signature-cancel-button"
          >
            Abbrechen
          </button>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={disabled}
          className="btn-primary text-xs"
          data-testid="signature-confirm-button"
        >
          {confirmLabel}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Mit dem Bestätigen Ihrer Unterschrift erklären Sie sich mit den oben aufgeführten
        Inhalten einverstanden.
      </p>
    </div>
  );
};

export default SignaturePad;
